import type { AgentResponseMessage } from "@/features/ai-agent/types/agent";
import type {
  AiPending,
  MessageGenerationStep,
  PendingApprovalSnapshot,
  PendingDraftSnapshot,
} from "@/features/ai-agent/types/agent-step";
import type {
  ReplyRating,
  ReplyReview,
  ReplyReviewReason,
} from "@/features/ai-agent/types/reply-review";
import type {
  DiscardDraftParams,
  DraftFeedbackParams,
  SendResponseStepParams,
  StepActionResult,
  SteerDraftParams,
} from "@/features/ai-agent/types/step-actions";
import type { RequestDraftParams } from "@/features/ai-agent/types/draft-request";

import { isDemoRecording } from "@/mock/config";
import { useMockStore } from "@/mock/store/mockStore";
import { useAgentStepsStore } from "@/stores/agent-steps";
import {
  replyBubblesOf,
  sameBubbles,
} from "@/features/ai-agent/helpers/agent-reply-bubbles";
import { asReplyOutput } from "@/features/ai-agent/helpers/agent-step-predicates";
import { suggestedMessagesBody } from "@/features/ai-agent/helpers/draft-bubbles";
import { useGlobalStore } from "@/stores/global";

const reviews = new Map<string, ReplyReview>();

/** Seeded so a preview can open on a reply that already exists in the ledger. */
export const seedMockReplyReview = (
  generationId: string,
  draftMessages: AgentResponseMessage[],
  delivery: ReplyReview["delivery"] = "auto",
) => {
  reviews.set(generationId, {
    id: `review-${generationId}`,
    generationId,
    delivery,
    outcome: delivery === "auto" ? "auto_sent" : null,
    draftMessages,
    finalMessages: delivery === "auto" ? draftMessages : null,
    rating: null,
    reasons: null,
    comment: null,
    suggestedMessages: null,
    ratedByUserId: null,
    ratedAt: null,
  });
};

export const getMockReplyReview = (generationId: string): ReplyReview | null =>
  reviews.get(generationId) ?? null;

/** The mock keeps one contact, so every rated row belongs to it. */
export const getMockContactReviews = (): ReplyReview[] =>
  [...reviews.values()].filter((review) => review.rating);

export const setMockReplyReview = (
  generationId: string,
  body: {
    rating: ReplyRating;
    reasons?: ReplyReviewReason[];
    comment?: string;
    suggestedMessages?: { type: string; text: string }[];
  },
): ReplyReview => {
  const existing = reviews.get(generationId);
  const next: ReplyReview = {
    ...(existing ?? {
      id: `review-${generationId}`,
      generationId,
      delivery: "auto",
      outcome: "auto_sent",
      draftMessages: [],
      finalMessages: [],
    }),
    rating: body.rating,
    reasons: body.reasons ?? [],
    comment: body.comment ?? null,
    suggestedMessages: body.suggestedMessages
      ? body.suggestedMessages.map((m) => ({ type: "text", text: m.text }))
      : null,
    ratedByUserId: useMockStore.getState().currentUserId,
    ratedAt: new Date().toISOString(),
  };

  reviews.set(generationId, next);

  return next;
};

const DRAFT_BUBBLES: AgentResponseMessage[] = [
  {
    type: "text",
    text: "Hi Priya, thanks for sending the photo. The patch test itself takes about ten minutes and there is no downtime.",
  },
  {
    type: "text",
    text: "We have Thursday 26 June at 10:00 or Friday at 15:30 free with Dr. Anaya Rao. Would either of those work for you?",
  },
  {
    type: "image",
    text: "",
    mediaUrl: "https://picsum.photos/seed/replyn-clinic/480/320",
  },
  {
    type: "text",
    text: "Here is the treatment room, so you know what to expect when you come in.",
  },
];

/** Asks and drafts land 45s after the inbound they answer. */
const echoStampAfter = (inbound: { createdAt: string }): string =>
  new Date(new Date(inbound.createdAt).getTime() + 45_000).toISOString();

const lastInbound = (contactId: string) =>
  [...(useMockStore.getState().messages.get(contactId) ?? [])]
    .reverse()
    .find((message) => message.owner === "contact");

/** Patches a mock contact's `aiPending` the way the backend's `syncPending`
 *  writes it: empty keys omitted, null once nothing is left. */
const patchMockAiPending = (
  contactId: string,
  patch: (pending: AiPending) => AiPending,
) => {
  const store = useMockStore.getState();
  const current =
    store.contacts.find((contact) => contact.id === contactId)?.aiPending ?? {};
  const next = patch({ ...current });

  if (!next.draft) delete next.draft;
  if (!next.questions?.length) delete next.questions;
  if (!next.approvals?.length) delete next.approvals;
  store.updateContact(contactId, {
    aiPending: Object.keys(next).length > 0 ? next : null,
  });
};

const withoutApprovalsOf =
  (generationId: string | null) =>
  (pending: AiPending): AiPending => ({
    ...pending,
    approvals: pending.approvals?.filter(
      (approval) => approval.generationId !== generationId,
    ),
  });

/** What the backend mirrors onto `contacts.aiPending.draft`. */
const toMockDraftSnapshot = (
  step: MessageGenerationStep,
): PendingDraftSnapshot => {
  const output = asReplyOutput(step);

  return {
    stepId: step.id,
    generationId: step.generationId,
    messageId: step.messageId,
    text: output?.text ?? "",
    media: output?.media ?? [],
    confidence: output?.confidence ?? 0,
    messages: replyBubblesOf(step),
    createdAt: step.createdAt,
  };
};

const contactWithDraft = (stepId: string) =>
  useMockStore
    .getState()
    .contacts.find((contact) => contact.aiPending?.draft?.stepId === stepId);

/** The contact's Echo flags as the list reads them, set once the store is
 *  seeded: the second contact's parked asks, the third's draft, and a question
 *  for the viewer on the fourth. The question is the flag alone: opening that
 *  conversation shows no question card. */
export const seedMockEchoFlags = () => {
  const store = useMockStore.getState();
  const [, asking, drafted, questioned] = store.contacts;
  const viewerId = useGlobalStore.getState().loggedInUser?.id;

  if (questioned) {
    patchMockAiPending(questioned.id, (pending) => ({
      ...pending,
      questions: [
        {
          stepId: `step-mock-question-${questioned.id}`,
          generationId: `gen-mock-question-${questioned.id}`,
          question:
            "Their insurance card looks expired. Book the consult anyway, or ask them for a new one first?",
          options: ["Book anyway", "Ask for a new card"],
          answerType: "select",
          askedUserIds: viewerId ? [viewerId] : [],
          createdAt: new Date(Date.now() - 6 * 60_000).toISOString(),
        },
      ],
    }));
  }
  const askedAfter = asking && lastInbound(asking.id);

  if (asking && askedAfter) {
    const generationId = `gen-mock-ask-${asking.id}`;
    const createdAt = echoStampAfter(askedAfter);
    const approvals: PendingApprovalSnapshot[] = [
      {
        stepId: `${generationId}-book`,
        generationId,
        toolId: "create_appointment",
        title: "Book an appointment",
        createdAt,
      },
      {
        stepId: `${generationId}-follow-up`,
        generationId,
        toolId: "create_follow_up",
        title: "Set a follow-up",
        createdAt,
      },
    ];

    patchMockAiPending(asking.id, (pending) => ({ ...pending, approvals }));
  }
  const [draft] = drafted ? getMockPendingDrafts(drafted.id) : [];

  if (drafted && draft) {
    patchMockAiPending(drafted.id, (pending) => ({
      ...pending,
      draft: toMockDraftSnapshot(draft),
    }));
  }
};

/** Two actions parked on a person, the way an inbox step event parks them,
 *  so the permission card is reachable in mock mode: on the second mock
 *  contact, which Echo owns and which carries no draft (the draft is written
 *  after the answer). */
const seedMockApprovals = (
  contactId: string,
  messageId: string,
  generationId: string,
  createdAt: string,
) => {
  const base = {
    type: "execute_tool" as const,
    output: null,
    messageId,
    generationId,
    status: "waiting" as const,
    error: null,
    iteration: 2,
    resolvedByUserId: null,
    resolvedAt: null,
    startedAt: null,
    completedAt: null,
    createdAt,
  };
  const steps: MessageGenerationStep[] = [
    {
      ...base,
      id: `${generationId}-book`,
      input: { type: "tool", toolId: "create_appointment" },
      reason:
        "They asked for the first free slot this week, and Thursday at 10 is it.",
      meta: {
        title: "Waiting on approval to book an appointment",
        requiresApproval: true,
        gate: "configured",
        actionType: "create_appointment",
        fields: [
          { label: "Doctor", value: "Dr. Anaya Rao" },
          { label: "Treatment", value: "Patch test" },
          { label: "When", value: "Thu, 26 Jun 2026 · 10:00 AM" },
        ],
      },
    },
    {
      ...base,
      id: `${generationId}-follow-up`,
      input: { type: "tool", toolId: "create_follow_up" },
      reason: "So someone checks Thursday still suits them before the day.",
      meta: {
        title: "Waiting on approval to set a follow-up",
        requiresApproval: true,
        gate: "check",
        check: { probability: 0.41, threshold: 0.7 },
        actionType: "create_follow_up",
        fields: [
          { label: "Due", value: "Tomorrow · 10:00 AM" },
          { label: "Note", value: "Ask whether Thursday still works" },
        ],
      },
    },
  ];

  for (const step of steps) {
    useAgentStepsStore.getState().applyStepEvent({
      contactId,
      conversationId: null,
      source: "inbox",
      correlationId: messageId,
      step,
    });
  }
};

/** Mock GET steps?contactId: the second contact answers with its parked
 *  asks (seeded into the store, nothing pending to return), the third with
 *  its drafted reply. */
export const getMockPendingSteps = (
  contactId: string,
): MessageGenerationStep[] => {
  const store = useMockStore.getState();

  if (store.contacts[1]?.id === contactId) {
    const inbound = lastInbound(contactId);
    const generationId = `gen-mock-ask-${contactId}`;
    const seeded = useAgentStepsStore
      .getState()
      .stepsByKey[
        inbound?.id ?? ""
      ]?.some((step) => step.generationId === generationId);

    if (inbound && !seeded) {
      seedMockApprovals(
        contactId,
        inbound.id,
        generationId,
        echoStampAfter(inbound),
      );
    }

    return [];
  }

  return getMockPendingDrafts(contactId);
};

/** A drafted reply awaiting review, on the third mock contact only, anchored on
 *  its newest inbound message the way the backend anchors a run. */
export const getMockPendingDrafts = (
  contactId: string,
): MessageGenerationStep[] => {
  const store = useMockStore.getState();

  if (store.contacts[2]?.id !== contactId) return [];
  const inbound = [...(store.messages.get(contactId) ?? [])]
    .reverse()
    .find((message) => message.owner === "contact");

  if (!inbound) return [];
  const generationId = `gen-mock-draft-${contactId}`;
  const existing = (
    useAgentStepsStore.getState().stepsByKey[inbound.id] ?? []
  ).filter((step) => step.generationId?.startsWith(generationId));

  if (existing.length) {
    return existing.filter(
      (step) => step.type === "response" && step.status === "waiting",
    );
  }
  const bubbles: AgentResponseMessage[] = isDemoRecording()
    ? [
        {
          type: "text",
          text: `Hi ${store.contacts[2]?.firstName || "there"}, thanks for getting in touch. I can help arrange a consultation.`,
        },
        {
          type: "text",
          text: "Which day and time would suit you? Our team will confirm the available appointment times.",
        },
      ]
    : DRAFT_BUBBLES;
  const createdAt = new Date(
    new Date(inbound.createdAt).getTime() + 45_000,
  ).toISOString();

  if (!reviews.has(generationId)) {
    seedMockReplyReview(generationId, bubbles, "draft");
  }

  return [
    {
      id: `${generationId}-response`,
      type: "response",
      input: null,
      output: {
        text: bubbles
          .filter((b) => !b.mediaUrl)
          .map((b) => b.text)
          .join("\n\n"),
        media: bubbles
          .filter((b) => b.mediaUrl)
          .map((b) => ({
            type: b.type,
            url: b.mediaUrl!,
          })),
        confidence: 0.81,
      },
      messageId: inbound.id,
      generationId,
      status: "waiting",
      error: null,
      iteration: null,
      resolvedByUserId: null,
      resolvedAt: null,
      startedAt: null,
      completedAt: null,
      meta: { messages: bubbles },
      createdAt,
    },
  ];
};

/** How long the resumed run "writes" before its draft lands. */
const MOCK_RESUME_WRITE_MS = 2_500;

/** The backend resumes a run once its last ask is answered: the card works,
 *  then lands on the draft. Mock mode replays that so the permission card
 *  hands over to the same states it does live. Only inbox asks resume here;
 *  preview runs answer through the chat panel. */
const resumeMockRun = (step: MessageGenerationStep) => {
  const store = useAgentStepsStore.getState();
  const contactId = Object.keys(store.waitingApprovalStepsByContact).find(
    (id) => store.waitingApprovalStepsByContact[id].includes(step.id),
  );
  const stillWaiting = (store.stepsByKey[step.messageId ?? ""] ?? []).some(
    (sibling) =>
      sibling.id !== step.id &&
      sibling.generationId === step.generationId &&
      sibling.status === "waiting",
  );

  if (!contactId || stillWaiting) return;
  patchMockAiPending(contactId, withoutApprovalsOf(step.generationId));
  if (!step.messageId) return;
  const correlationId = step.messageId;
  const emit = (next: MessageGenerationStep) =>
    store.applyStepEvent({
      contactId,
      conversationId: null,
      source: "inbox",
      correlationId,
      step: next,
    });
  const generationId = step.generationId ?? correlationId;
  const startedAt = new Date().toISOString();
  const writing: MessageGenerationStep = {
    id: `${generationId}-response`,
    type: "response",
    input: null,
    output: null,
    messageId: correlationId,
    generationId,
    status: "running",
    error: null,
    iteration: null,
    resolvedByUserId: null,
    resolvedAt: null,
    startedAt,
    completedAt: null,
    meta: { title: "Writing the reply…" },
    createdAt: startedAt,
  };

  emit(writing);
  window.setTimeout(() => {
    seedMockReplyReview(generationId, DRAFT_BUBBLES, "draft");
    const draft: MessageGenerationStep = {
      ...writing,
      status: "waiting",
      output: {
        text: DRAFT_BUBBLES.filter((b) => !b.mediaUrl)
          .map((b) => b.text)
          .join("\n\n"),
        media: DRAFT_BUBBLES.filter((b) => b.mediaUrl).map((b) => ({
          type: b.type,
          url: b.mediaUrl!,
        })),
        confidence: 0.84,
      },
      completedAt: new Date().toISOString(),
      meta: { messages: DRAFT_BUBBLES },
    };

    emit(draft);
    store.applyPhaseEvent({
      contactId,
      conversationId: null,
      generationId,
      phase: "done",
    });
    patchMockAiPending(contactId, (pending) => ({
      ...pending,
      draft: toMockDraftSnapshot(draft),
    }));
  }, MOCK_RESUME_WRITE_MS);
};

export const resolveMockStep = (
  step: MessageGenerationStep,
  status: MessageGenerationStep["status"],
): StepActionResult => {
  const next = {
    ...step,
    status,
    resolvedAt: new Date().toISOString(),
    resolvedByUserId: useMockStore.getState().currentUserId,
  };

  const drafted = step.type === "response" ? contactWithDraft(step.id) : null;

  if (drafted && status !== "waiting") {
    patchMockAiPending(drafted.id, (pending) => ({
      ...pending,
      draft: undefined,
    }));
  }
  if (status === "denied" && step.meta?.requiresApproval) {
    const waiting = useAgentStepsStore.getState().waitingApprovalStepsByContact;
    const askedId = Object.keys(waiting).find((id) =>
      waiting[id].includes(step.id),
    );

    if (askedId) {
      patchMockAiPending(askedId, withoutApprovalsOf(step.generationId));
    }
  }
  useAgentStepsStore.getState().upsertStep(next);
  if (
    (status === "approved" || status === "answered") &&
    step.meta?.requiresApproval
  ) {
    resumeMockRun(step);
  }
  // A deny ends the run, and the backend supersedes whatever else was parked
  // beside the denied step; the store gets those flips as step events.
  if (status === "denied" && step.meta?.requiresApproval) {
    const store = useAgentStepsStore.getState();
    const key = step.messageId ?? step.generationId ?? "";

    for (const sibling of store.stepsByKey[key] ?? []) {
      if (
        sibling.id !== step.id &&
        sibling.generationId === step.generationId &&
        sibling.status === "waiting"
      ) {
        store.upsertStep({ ...sibling, status: "superseded" });
        store.resolveWaitingApproval(sibling.id);
      }
    }
  }
  const review = reviews.get(step.generationId ?? "");

  if (review && step.type === "response") {
    reviews.set(review.generationId, {
      ...review,
      outcome:
        status === "superseded"
          ? "steered"
          : status === "denied"
            ? "discarded"
            : "sent",
      outcomeAt: next.resolvedAt,
      finalMessages: status === "executed" ? replyBubblesOf(step) : null,
    });
  }

  return { success: true, step: next };
};

/** A waiting reply step rewritten as the given bubbles, the way the backend
 *  files a new draft: same run, fresh `waiting`, nobody has resolved it. */
const waitingReplyOf = (
  step: MessageGenerationStep,
  bubbles: AgentResponseMessage[],
  over: Partial<MessageGenerationStep>,
): MessageGenerationStep => ({
  ...step,
  status: "waiting",
  resolvedAt: null,
  resolvedByUserId: null,
  createdAt: new Date().toISOString(),
  output: {
    text: bubbles
      .filter((bubble) => !bubble.mediaUrl)
      .map((bubble) => bubble.text)
      .join("\n\n"),
    media: bubbles
      .filter((bubble) => bubble.mediaUrl)
      .map((bubble) => ({ type: bubble.type, url: bubble.mediaUrl! })),
    confidence: asReplyOutput(step)?.confidence ?? 0.81,
  },
  meta: { ...step.meta, messages: bubbles },
  ...over,
});

export const feedbackMockDraft = ({
  step,
  rating,
  reasons,
  comment,
  suggestedText,
}: DraftFeedbackParams) => ({
  success: true,
  step,
  review: setMockReplyReview(step.generationId ?? step.id, {
    rating,
    reasons,
    comment,
    ...suggestedMessagesBody(suggestedText),
  }),
});

export const discardMockDraft = ({ step, ...verdict }: DiscardDraftParams) => {
  if (verdict.reasons?.length || verdict.comment || verdict.suggestedText) {
    feedbackMockDraft({
      step,
      contactId: "",
      rating: "down",
      reasons: [],
      comment: "",
      suggestedText: "",
      ...verdict,
    });
  }

  return resolveMockStep(step, "denied");
};

export const sendMockResponseStep = ({
  step,
  contactId,
  messageIds,
  handBackToAi,
  messages,
}: SendResponseStepParams) => {
  const result = resolveMockStep(step, "executed");
  const store = useMockStore.getState();

  if (contactId) {
    patchMockAiPending(contactId, (pending) => ({
      ...pending,
      draft: undefined,
    }));
    store.updateContact(contactId, {
      lastMessageProcessedAt: result.step.resolvedAt!,
      lastMessageOwner: "user",
      ...(handBackToAi ? { aiMode: "sends" as const } : {}),
    });
  }
  const review = reviews.get(step.generationId ?? "");

  if (review) {
    // The person's own words in the draft's place: the draft settles as
    // edited with what went out; the step keeps Echo's.
    const own =
      messages && !sameBubbles(messages, replyBubblesOf(step))
        ? messages
        : null;

    reviews.set(review.generationId, {
      ...review,
      ...(own ? { outcome: "edited_sent", finalMessages: own } : {}),
      deliveredMessageIds: messageIds,
    });
  }

  return { ...result, handedBackToAi: Boolean(handBackToAi && contactId) };
};

export const steerMockDraft = ({ step }: SteerDraftParams) => {
  const drafted = contactWithDraft(step.id);

  resolveMockStep(step, "superseded");
  const generationId = `${step.generationId}-redraft-${crypto.randomUUID()}`;
  const messages = replyBubblesOf(step).map((bubble) => ({
    ...bubble,
    text: bubble.text
      .replace("thanks for sending the photo", "thank you for the photo")
      .replace(
        "The patch test itself takes about ten minutes and there is no downtime.",
        "The patch test takes just ten minutes, with no downtime.",
      )
      .replace(
        "Would either of those work for you?",
        "Which time would suit you best?",
      ),
  }));
  // The model's own step of a new run: no input, the note rides the ask step.
  const next = waitingReplyOf(step, messages, {
    id: `${generationId}-response`,
    generationId,
    input: null,
  });

  seedMockReplyReview(generationId, messages, "draft");
  useAgentStepsStore.getState().upsertStep(next);
  if (drafted) {
    patchMockAiPending(drafted.id, (pending) => ({
      ...pending,
      draft: toMockDraftSnapshot(next),
    }));
  }

  return { success: true };
};

/** Nothing to send back here: the banner's instruction mints a draft from
 *  scratch, anchored on whatever the conversation's last message was. The
 *  operator's own words stand in for the writing the model would do. */
export const instructMockDraft = ({
  contactId,
  instruction,
}: RequestDraftParams) => {
  const store = useMockStore.getState();
  const messages = store.messages.get(contactId) ?? [];
  const anchor = messages[messages.length - 1];

  if (!anchor) return { queued: false };

  const generationId = `gen-mock-instructed-${crypto.randomUUID()}`;
  const contact = store.contacts.find((one) => one.id === contactId);
  const bubbles: AgentResponseMessage[] = [
    {
      type: "text",
      text: `Hi ${contact?.firstName || "there"}, quick update from us.`,
    },
    { type: "text", text: instruction },
  ];

  seedMockReplyReview(generationId, bubbles, "draft");
  const draft: MessageGenerationStep = {
    id: `${generationId}-response`,
    type: "response",
    input: null,
    output: {
      text: bubbles.map((bubble) => bubble.text).join("\n\n"),
      media: [],
      confidence: 0.86,
    },
    messageId: anchor.id,
    generationId,
    status: "waiting",
    error: null,
    iteration: null,
    resolvedByUserId: null,
    resolvedAt: null,
    startedAt: null,
    completedAt: null,
    meta: { messages: bubbles },
    createdAt: new Date().toISOString(),
  };

  useAgentStepsStore.getState().upsertStep(draft);
  patchMockAiPending(contactId, (pending) => ({
    ...pending,
    draft: toMockDraftSnapshot(draft),
  }));

  return { queued: true };
};
